import { useEffect, useState } from 'react';
import { useAuth } from '@/App';
import {
  createOrder,
  getProduct,
  saveCheckoutDraft,
  lookupPincode,
  submitPayment,
  trackAnalyticsEvent,
} from '@/lib/api';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import {
  ShoppingCart,
  Trash2,
  Minus,
  Plus,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';

import { toast } from 'sonner';


const UNAVAILABLE_MESSAGE = 'We apologize. This product is not yet available in this region';
const normalizeRegion = value => String(value || '').trim().toLowerCase().replace(/&/g, 'and');
const isProductAvailable = (product, state) => {
  if (product.general_availability !== false) return true;
  const region = normalizeRegion(state);
  const aliases = { 'nct of delhi': 'delhi', 'orissa': 'odisha', 'pondicherry': 'puducherry', 'dadra and nagar haveli': 'dadra and nagar haveli and daman and diu', 'daman and diu': 'dadra and nagar haveli and daman and diu' };
  const normalized = aliases[region] || region;
  return (product.available_states || []).some(item => normalizeRegion(item) === normalized);
};

const PAYMENT_PHONE = '+917204438810';

const CartDrawer = ({ open, onOpenChange, checkoutRequest = 0 }) => {
  const {
    cart,
    updateCartQuantity,
    updateCartVariant,
    removeFromCart,
    clearCart,
  } = useAuth();

  const [step, setStep] = useState('cart');
  const [loading, setLoading] = useState(false);
  const [pincodeStatus, setPincodeStatus] = useState('');
  const [createdOrders, setCreatedOrders] = useState([]);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [resolvedPincode, setResolvedPincode] = useState('');

  useEffect(() => {
    if (open && checkoutRequest > 0) {
      setStep('checkout');
    }
  }, [open, checkoutRequest]);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    pincode: '',
  });

  // Persist partial customer details in MongoDB as they are entered.
  // This draft is not an order and does not reserve stock.
  const [draftId] = useState(() => {
    const key = '0middle_checkout_draft_id';
    let id = sessionStorage.getItem(key);
    if (!id) {
      id = window.crypto?.randomUUID?.() || `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(key, id);
    }
    return id;
  });
  useEffect(() => {
    if (!open || (step !== 'checkout' && step !== 'payment') || !cart.length) return;
    const timer = setTimeout(() => {
      saveCheckoutDraft({
        draft_id: draftId,
        customer_name: form.name,
        customer_phone: form.phone,
        customer_email: form.email.trim(),
        delivery_address: {
          line1: form.line1, line2: form.line2, city: form.city,
          state: form.state, pincode: form.pincode,
        },
        items: cart.map(item => ({
          product_slug: item.product.slug,
          product_name: item.product.name,
          variant_id: item.product.selected_variant?.variant_id || null,
          pack_size: item.product.pack_size,
          quantity: item.quantity,
        })),
      }).catch(error => console.error('Checkout draft autosave failed', error));
    }, 650);
    return () => clearTimeout(timer);
  }, [open, step, form, cart, draftId]);

  const [errors, setErrors] = useState({
    name: '',
    phone: '',
    line1: '',
    city: '',
    state: '',
    pincode: '',
  });

  const productTotalPrice = (product) => {
    if (product.total_price !== undefined && product.total_price !== null) {
      return Number(product.total_price);
    }

    return (
      Number(product.farmer_price || 0) +
      Number(product.shipping || 0) +
      Number(product.packaging ?? product.handling ?? 0) +
      Number(product.testing || 0)
    );
  };

  const productTransactionCost = (product) =>
    Number(product.platform_fee || 0);

  const productListPrice = (product) => {
    if (product.list_price !== undefined && product.list_price !== null) {
      return Number(product.list_price);
    }

    return productTotalPrice(product) + productTransactionCost(product);
  };

  const farmerTotal = cart.reduce(
    (sum, item) =>
      sum + productTotalPrice(item.product) * item.quantity,
    0
  );

  const transactionCostTotal = cart.reduce(
    (sum, item) =>
      sum + productTransactionCost(item.product),
    0
  );

  const platformDiscountTotal = cart.reduce(
    (sum, item) => sum + Number(item.product.platform_fee_discount || 0),
    0
  );
  const originalPlatformFeeTotal = transactionCostTotal + platformDiscountTotal;

  const total = cart.reduce(
    (sum, item) =>
      sum + productTotalPrice(item.product) * item.quantity + productTransactionCost(item.product),
    0
  );

  const handleChange = (e) => {
    const { name, value } = e.target;

    /*
      NAME
      Letters and spaces only.
      If somebody types a number or special character,
      we simply ignore it and gently explain why.
    */
    if (name === 'name') {
      const cleaned = value.replace(/[^A-Za-z\s]/g, '');

      setForm((previous) => ({
        ...previous,
        name: cleaned,
      }));

      setErrors((previous) => ({
        ...previous,
        name:
          value !== cleaned
            ? 'Please use letters and spaces only.'
            : '',
      }));

      return;
    }

    /*
      MOBILE NUMBER
      Numbers only.
      Maximum 10 digits.
    */
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      const cleaned = digitsOnly.slice(0, 10);

      setForm((previous) => ({
        ...previous,
        phone: cleaned,
      }));

      let phoneMessage = '';

      if (value !== digitsOnly) {
        phoneMessage = 'Please use numbers only.';
      } else if (cleaned.length > 0 && cleaned.length < 10) {
        phoneMessage = 'Please enter a 10-digit mobile number.';
      }

      setErrors((previous) => ({
        ...previous,
        phone: phoneMessage,
      }));

      return;
    }

    /*
      PIN CODE
      Numbers only.
      Maximum 6 digits.
    */
    if (name === 'pincode') {
      const digitsOnly = value.replace(/\D/g, '');
      const cleaned = digitsOnly.slice(0, 6);

      setForm((previous) => ({
        ...previous,
        pincode: cleaned,
      }));

      let pincodeMessage = '';

      if (value !== digitsOnly) {
        pincodeMessage = 'Please use numbers only.';
      } else if (cleaned.length > 0 && cleaned.length < 6) {
        pincodeMessage = 'Please enter a 6-digit PIN code.';
      }

      setErrors((previous) => ({
        ...previous,
        pincode: pincodeMessage,
      }));

      if (cleaned.length < 6) {
        setResolvedPincode('');
        setPincodeStatus('');
      }

      if (cleaned.length === 6) {
        setPincodeStatus('Finding city and state...');

        lookupPincode(cleaned)
          .then((response) => {
            const location = response.data;
            setResolvedPincode(cleaned);

            setForm((previous) => {
              if (previous.pincode !== cleaned) {
                return previous;
              }

              return {
                ...previous,
                city: location.city || previous.city,
                state: location.state || previous.state,
              };
            });

            setErrors((previous) => ({
              ...previous,
              pincode: '',
            }));

            const unavailable = cart.filter(item => !isProductAvailable(item.product, location.state));
            setPincodeStatus(unavailable.length ? UNAVAILABLE_MESSAGE : 'City and state filled from PIN code.');
          })
          .catch(() => {
            setResolvedPincode('');
            setPincodeStatus('');
            setErrors((previous) => ({
              ...previous,
              pincode:
                'We could not find this PIN code. Please enter city and state manually.',
            }));
          });
      }

      return;
    }

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: '',
    }));
  };

  const validate = () => {
    const newErrors = {
      name: '',
      phone: '',
      email: '',
      line1: '',
      city: '',
      state: '',
      pincode: '',
    };

    let valid = true;

    if (!form.name.trim()) {
      newErrors.name = 'Please enter your name.';
      valid = false;
    }

    if (!/^[A-Za-z\s]+$/.test(form.name.trim())) {
      newErrors.name = 'Please use letters and spaces only.';
      valid = false;
    }

    if (!/^\d{10}$/.test(form.phone)) {
      newErrors.phone = 'Please enter a 10-digit mobile number.';
      valid = false;
    }

    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = 'Please enter a valid email address for order confirmation.';
      valid = false;
    }

    if (!form.line1.trim()) {
      newErrors.line1 = 'Please enter your delivery address.';
      valid = false;
    }

    if (!form.city.trim()) {
      newErrors.city = 'Please enter your city.';
      valid = false;
    }

    if (!form.state.trim()) {
      newErrors.state = 'Please enter your state.';
      valid = false;
    }

    if (!/^\d{6}$/.test(form.pincode)) {
      newErrors.pincode = 'Please enter a 6-digit PIN code.';
      valid = false;
    }

    setErrors(newErrors);

    return valid;
  };

  // Create and persist the order BEFORE displaying UPI instructions.
  // A customer closing the browser after payment must not lose the order.
  const continueToPayment = async () => {
    if (!validate() || loading) {
      if (!loading) toast.error('Please check the highlighted details.');
      return;
    }
    setLoading(true);
    setCheckoutError('');
    try {
      if (cart.some(item => item.product.general_availability === false)) {
        const pinResult = await lookupPincode(form.pincode);
        const unavailable = cart.filter(item => !isProductAvailable(item.product, pinResult.data.state));
        if (unavailable.length) { setCheckoutError(UNAVAILABLE_MESSAGE); setPincodeStatus(UNAVAILABLE_MESSAGE); return; }
      }

      // Validate the live product catalogue before creating any orders.
      // An Admin may have removed a pack size since it was added to the cart.
      const liveProducts = await Promise.all(cart.map((item) => getProduct(item.product.slug)));
      for (let i = 0; i < cart.length; i += 1) {
        const item = cart[i];
        const currentProduct = liveProducts[i].data;
        const selectedId = item.product.selected_variant?.variant_id;
        const currentVariants = currentProduct.variants || [];
        const stillOffered = selectedId
          ? currentVariants.some((variant) => variant.variant_id === selectedId)
          : currentVariants.length === 0;
        if (!currentProduct.active || !stillOffered) {
          const message = `The selected pack size for ${item.product.name} is no longer available. Remove it from your cart and select an available pack size.`;
          setCheckoutError(message);
          toast.error(message);
          return;
        }
      }

      // A page transition is blocked until the full customer form is durably saved.
      // Debounced background saves alone are not sufficient: navigation can cancel them.
      await saveCheckoutDraft({
        draft_id: draftId,
        customer_name: form.name.trim(),
        customer_phone: form.phone,
        delivery_address: {
          line1: form.line1.trim(), line2: form.line2.trim(),
          city: form.city.trim(), state: form.state.trim(), pincode: form.pincode,
        },
        items: cart.map(item => ({
          product_slug: item.product.slug,
          product_name: item.product.name,
          variant_id: item.product.selected_variant?.variant_id || null,
          pack_size: item.product.pack_size,
          quantity: item.quantity,
        })),
      });
      const orders = [...createdOrders];
      for (let i = orders.length; i < cart.length; i += 1) {
        const item = cart[i];
        const response = await createOrder({
          customer_name: form.name.trim(),
          customer_phone: form.phone,
          delivery_address: {
            line1: form.line1.trim(),
            line2: form.line2.trim() || null,
            city: form.city.trim(),
            state: form.state.trim(),
            pincode: form.pincode,
          },
          product_slug: item.product.slug,
          variant_id: item.product.selected_variant?.variant_id || null,
          quantity: item.quantity,
        });
        orders.push(response.data);
        // Preserve partial successes so retrying cannot duplicate those orders.
        setCreatedOrders([...orders]);
      }
      trackAnalyticsEvent('PAYMENT_VIEW', {
        cart_items: cart.length,
        total,
        order_ids: orders.map((order) => order.order_id),
      }).catch(() => {});
      setStep('payment');
    } catch (error) {
      console.error(error);
      const message = error?.response?.data?.detail || 'Could not save your order. Please do not pay yet.';
      setCheckoutError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const confirmPaid = async () => {
    if (!createdOrders.length || paymentSubmitting) return;
    setPaymentSubmitting(true);
    try {
      await Promise.all(createdOrders.map((order) => submitPayment(order.order_id)));
      clearCart();
      setPaymentSubmitted(true);
      trackAnalyticsEvent('PURCHASE_DECLARED', {
        order_ids: createdOrders.map((order) => order.order_id), total,
      }).catch(() => {});
      setStep('created');
      toast.success('Payment declaration recorded.');
    } catch (error) {
      console.error(error);
      toast.error('Order is saved. Please retry payment confirmation or contact +91 72044 38810 with your order number.');
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const closeDrawer = () => {
    onOpenChange(false);

    setTimeout(() => {
      setStep('cart');
      setCreatedOrders([]);
      setPaymentSubmitted(false);
    }, 250);
  };

  return (
    <Sheet open={open} onOpenChange={closeDrawer}>
      <SheetContent
        className="w-full sm:max-w-lg flex flex-col"
        overlayClassName="bg-transparent"
      >

        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />

            {step === 'cart' && 'Your Cart'}
            {step === 'checkout' && 'Delivery Details'}
            {step === 'payment' && 'Payment'}
            {step === 'created' && 'Order Created'}
          </SheetTitle>
        </SheetHeader>

        {step === 'cart' && (
          <>
            {cart.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-center">
                <div>
                  <ShoppingCart className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />

                  <p className="text-muted-foreground">
                    Your cart is empty
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-auto py-4 space-y-4">

                  {cart.map((item) => (
                    <div
                      key={`${item.product.slug}-${item.product.selected_variant?.variant_id || 'default'}`}
                      className="border rounded-xl p-4"
                    >
                      <div className="flex justify-between gap-3">

                        <div>
                          <p className="font-semibold">
                            {item.product.name}
                          </p>

                          <div className="mt-1">
                            {(item.product.variants?.length || 0) > 1 ? (
                              <div role="radiogroup" aria-label={`Pack size for ${item.product.name}`} className="flex flex-wrap items-center gap-3">
                                {item.product.variants.map((variant) => {
                                  const selected = (item.product.selected_variant?.variant_id || item.product.variants[0].variant_id) === variant.variant_id;
                                  const price = variant.list_price ?? (
                                    Number(variant.farmer_price || 0) +
                                    Number(variant.packaging ?? variant.handling ?? 0) +
                                    Number(variant.shipping || 0) +
                                    Number(variant.testing || 0) +
                                    Number(variant.platform_fee || 0)
                                  );
                                  return (
                                    <label key={variant.variant_id} className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors ${selected ? 'border-primary bg-primary/5 font-semibold' : 'border-border hover:bg-muted/50'}`}>
                                      <input
                                        type="radio"
                                        name={`cart-pack-${item.product.slug}`}
                                        value={variant.variant_id}
                                        checked={selected}
                                        onChange={() => {
                                          if (!selected) updateCartVariant(item.product.slug, item.product.selected_variant?.variant_id || item.product.variants[0].variant_id, variant.variant_id);
                                        }}
                                        className="accent-primary"
                                      />
                                      <span>{variant.pack_size} · ₹{price}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                {item.product.pack_size}
                              </p>
                            )}
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            removeFromCart(
                              item.product.slug,
                              item.product.selected_variant?.variant_id || 'default'
                            )
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>

                      </div>

                      <div className="flex items-center justify-between mt-4">

                        <div className="flex items-center border rounded-lg">

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              updateCartQuantity(
                                item.product.slug,
                                item.product.selected_variant?.variant_id || 'default',
                                item.quantity - 1
                              )
                            }
                          >
                            <Minus className="w-4 h-4" />
                          </Button>

                          <span className="px-3">
                            {item.quantity}
                          </span>

                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={
                              item.quantity >=
                              Number(item.product.sellable_pack_count || 0)
                            }
                            onClick={() =>
                              updateCartQuantity(
                                item.product.slug,
                                item.product.selected_variant?.variant_id || 'default',
                                item.quantity + 1
                              )
                            }
                          >
                            <Plus className="w-4 h-4" />
                          </Button>

                        </div>

                        <span className="font-semibold">
                          ₹
                          {productTotalPrice(item.product) *
                            item.quantity + productTransactionCost(item.product)}
                        </span>

                      </div>

                      <p className="text-xs text-muted-foreground mt-2">
                        {item.product.inventory_available} {item.product.inventory_unit} available
                        {item.product.sellable_pack_count != null
                          ? ` · ${item.product.sellable_pack_count} packs`
                          : ''}
                      </p>
                    </div>
                  ))}

                </div>

                <div className="border-t pt-4">

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span>To the Farmer</span>
                      <span>₹{farmerTotal}</span>
                    </div>

                    <div className="flex justify-between text-sm">
                      <span>0middle platform fee</span>
                      <span>₹{transactionCostTotal}</span>
                    </div>

                    {platformDiscountTotal > 0 && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span>Original platform fee</span>
                          <span className="line-through">₹{originalPlatformFeeTotal}</span>
                        </div>
                        <div className="flex justify-between text-sm text-green-700">
                          <span>0middle platform fee discount</span>
                          <span>−₹{platformDiscountTotal}</span>
                        </div>
                        <p className="text-xs text-green-700">0middle has reduced its platform fee. Farmer-side costs are unchanged.</p>
                      </>
                    )}
                    <div className="flex justify-between border-t pt-3 text-lg font-semibold">
                      <span>Total</span>
                      <span className="text-primary">
                        ₹{total}
                      </span>
                    </div>
                  </div>

                  <Button
                    className="w-full h-12"
                    onClick={() => {
                      trackAnalyticsEvent('CHECKOUT_STARTED', {
                        cart_items: cart.length,
                        total,
                      }).catch(() => {});
                      setStep('checkout');
                    }}
                  >
                    Checkout
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>

                </div>
              </>
            )}
          </>
        )}

        {step === 'checkout' && (
          <div className="flex-1 overflow-auto py-4 space-y-4">

            <div>
              <label className="text-sm font-medium">
                Name
              </label>

              <Input
                name="name"
                type="text"
                autoComplete="name"
                value={form.name}
                onChange={handleChange}
                className="mt-1"
              />

              {errors.name && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.name}
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">
                Mobile number
              </label>

              <Input
                name="phone"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                autoComplete="tel"
                value={form.phone}
                onChange={handleChange}
                className="mt-1"
              />

              {errors.phone && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.phone}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="checkout-email" className="text-sm font-medium">
                To receive your order confirmation
              </label>
              <Input
                id="checkout-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="Email address"
                maxLength={254}
                value={form.email}
                onChange={handleChange}
                aria-invalid={Boolean(errors.email)}
                className="mt-1"
              />
              {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="text-sm font-medium">
                PIN code
              </label>

              <Input
                name="pincode"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="postal-code"
                value={form.pincode}
                onChange={handleChange}
                className="mt-1"
              />

              {errors.pincode && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.pincode}
                </p>
              )}

              {!errors.pincode && pincodeStatus && (
                <p className="text-sm text-muted-foreground mt-1">
                  {pincodeStatus}
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">
                State
              </label>

              <Input
                name="state"
                autoComplete="address-level1"
                value={form.state}
                onChange={handleChange}
                className="mt-1"
              />

              {errors.state && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.state}
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">
                City
              </label>

              <Input
                name="city"
                autoComplete="address-level2"
                value={form.city}
                onChange={handleChange}
                className="mt-1"
              />

              {errors.city && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.city}
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">
                Address
              </label>

              <Input
                name="line1"
                autoComplete="address-line1"
                value={form.line1}
                onChange={handleChange}
                className="mt-1"
              />

              {errors.line1 && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.line1}
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">
                Apartment / landmark
              </label>

              <Input
                name="line2"
                autoComplete="address-line2"
                value={form.line2}
                onChange={handleChange}
                className="mt-1"
              />
            </div>

            <div className="border-t pt-4">

              {platformDiscountTotal > 0 && (
                <div className="mb-4 rounded-lg border p-3 text-sm space-y-1">
                  <div className="flex justify-between"><span>Original 0middle platform fee</span><span className="line-through">₹{originalPlatformFeeTotal}</span></div>
                  <div className="flex justify-between text-green-700"><span>0middle platform fee discount</span><span>−₹{platformDiscountTotal}</span></div>
                  <p className="text-xs text-muted-foreground">0middle has waived part or all of its platform fee. Farmer-side costs remain unchanged.</p>
                </div>
              )}
              <div className="flex justify-between font-semibold mb-4">
                <span>Order total</span>

                <span className="text-primary">
                  ₹{total}
                </span>
              </div>

              <Button
                className="w-full h-12"
                onClick={continueToPayment}
                disabled={loading}
              >
                {loading ? 'Saving your order...' : 'Continue to payment'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <Button
                variant="ghost"
                className="w-full mt-2"
                disabled={loading}
                onClick={() => setStep('cart')}
              >
                Back to Cart
              </Button>

            </div>

          </div>
        )}

        {step === 'payment' && (
          <div className="flex-1 overflow-auto py-4">
            <div>
              <h2 className="text-2xl font-semibold mb-5 text-center">
                Pay ₹{total}
              </h2>
              <p className="text-sm text-center mb-4">Order saved: {createdOrders.map(o => o.order_id).join(', ')}. Keep this number if you close the page.</p>

              <div className="border rounded-xl p-5 bg-white space-y-5">
                <div>
                  <p className="font-semibold">Step 1: Copy phone number</p>
                  <Button
                    type="button"
                    className="w-full mt-3 bg-amber-700 hover:bg-amber-800 text-white"
                    onClick={async () => {
                      await navigator.clipboard.writeText(PAYMENT_PHONE);
                      toast.success('Mobile number copied');
                    }}
                  >
                    Copy phone number
                  </Button>
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    படி 1: தொலைபேசி எண்ணை நகலெடுக்கவும்
                  </p>
                </div>

                <div className="border-t pt-5">
                  <p className="font-semibold">
                    Step 2: Open Google Pay / PhonePe and paste the phone number
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Pay ₹{total} using the phone number copied in Step 1.
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    படி 2: Google Pay / PhonePe செயலியைத் திறந்து, படி 1-ல் நகலெடுத்த தொலைபேசி எண்ணை ஒட்டி ₹{total} செலுத்தவும்
                  </p>
                </div>

                <div className="border-t pt-5">
                  <p className="font-semibold">Step 3: Click “I’ve paid” here</p>
                  <Button
                    className="w-full h-12 mt-3"
                    disabled={paymentSubmitting}
                    onClick={confirmPaid}
                  >
                    {paymentSubmitting ? 'Recording payment...' : 'I’ve paid'}
                  </Button>
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    படி 3: இங்கே “பணம் செலுத்திவிட்டேன்” என்பதை கிளிக் செய்யவும்
                  </p>
                </div>
              </div>

              <Button
                variant="ghost"
                className="w-full mt-2"
                disabled={paymentSubmitting}
                onClick={() => setStep('checkout')}
              >
                Back to delivery details
              </Button>
            </div>
          </div>
        )}

        {step === 'created' && (
          <div className="flex-1 overflow-auto py-4 text-center">

            <CheckCircle className="w-14 h-14 text-primary mx-auto mb-3" />

            <h2 className="text-2xl font-semibold mb-1">
              Order created
            </h2>

            <p className="text-muted-foreground mb-5">
              We will message you within 24 hours. For any questions, contact us at +91 72044 38810. 24 மணி நேரத்திற்குள் நாங்கள் உங்களுக்கு செய்தி அனுப்புவோம். ஏதேனும் கேள்விகள் இருந்தால், +91 72044 38810 என்ற எண்ணில் எங்களைத் தொடர்பு கொள்ளுங்கள்.
            </p>

            <div className="space-y-3">
              {createdOrders.map((order) => (
                <div
                  key={order.order_id}
                  className="border rounded-xl p-4"
                >
                  <p className="text-sm text-muted-foreground">
                    Order {order.order_id}
                  </p>

                  {order.pricing?.total !== undefined && (
                    <p className="text-3xl font-semibold mt-1">
                      ₹{order.pricing.total}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <Button
              variant="ghost"
              className="w-full mt-2"
              onClick={closeDrawer}
            >
              Done
            </Button>

          </div>
        )}

      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;